import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  TemplateRef,
  inject,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  AfterViewInit
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
  ValidatorFn,
  AbstractControl,
  FormControl,
  FormGroupDirective,
  NgForm
} from '@angular/forms';
import {
  MatDialog,
  MatDialogModule,
  MatDialogTitle,
  MatDialogContent,
  MatDialogActions
} from '@angular/material/dialog';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatPaginatorModule, MatPaginator } from '@angular/material/paginator';
import { ErrorStateMatcher } from '@angular/material/core';
import { RouterModule } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Perfil } from '../../models/perfil.interface';
import { PERFIL_LABELS } from './perfil.labels';
import * as PerfilActions from '../../store/perfil.actions';
import * as PerfilSelectors from '../../store/perfil.selectors';
import { ErrorHandlerService } from '../../../../shared/services/error-handler.service';
import { NotificationService } from '../../../../shared/services/notification.service';
import { LoggerService } from '../../../../core/services/logger.service';

@Component({
  selector: 'app-perfil',
  templateUrl: './perfil.component.html',
  styleUrls: ['./perfil.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatPaginatorModule,
    RouterModule
  ]
})
export class PerfilComponent implements OnInit, OnDestroy, AfterViewInit {
  private readonly store = inject(Store);
  private readonly dialog = inject(MatDialog);
  private readonly formBuilder = inject(FormBuilder);
  private readonly errorHandler = inject(ErrorHandlerService);
  private readonly notificationService = inject(NotificationService);
  private readonly logger = inject(LoggerService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroy$ = new Subject<void>();

  readonly labels = PERFIL_LABELS;

  // Observables del store
  perfiles$ = this.store.select(PerfilSelectors.selectAllPerfiles);
  loading$ = this.store.select(PerfilSelectors.selectPerfilLoading);
  error$ = this.store.select(PerfilSelectors.selectPerfilError);
  creating$ = this.store.select(PerfilSelectors.selectPerfilCreating);
  updating$ = this.store.select(PerfilSelectors.selectPerfilUpdating);
  deleting$ = this.store.select(PerfilSelectors.selectPerfilDeleting);

  // Filtros por columnas
  columnFilters: { nombrePerfil: string } = {
    nombrePerfil: ''
  };

  // Data source para la tabla
  dataSource = new MatTableDataSource<Perfil>([]);
  displayedColumns: string[] = ['perfilId', 'nombrePerfil', 'actions'];

  // Keep latest perfiles for validators
  private currentPerfiles: Perfil[] = [];

  // Propiedades para paginación
  pageSize = 10;
  pageSizeOptions = [5, 10, 25, 50];
  pageIndex = 0;

  // Propiedades para búsqueda
  searchQuery = '';
  showSearchPanel = false;

  // Formulario
  perfilForm: FormGroup;

  // Referencias a templates
  @ViewChild('createPerfilModal') createPerfilModal!: TemplateRef<unknown>;
  @ViewChild(MatPaginator, { static: false }) paginator!: MatPaginator;

  // Estado del componente
  selectedPerfil: Perfil | null = null;
  isEditing = false;

  // Variables para controlar notificaciones
  private wasCreating = false;
  private wasUpdating = false;
  private wasDeleting = false;
  private isCurrentlyDeleting = false;
  private deleteHadError = false;

  // Error state matcher para mostrar errores al escribir
  readonly errorStateMatcher: ErrorStateMatcher = new (class implements ErrorStateMatcher {
    isErrorState(control: FormControl | null, form: FormGroupDirective | NgForm | null): boolean {
      const isSubmitted = !!(form && form.submitted);
      return !!(control && control.invalid && (control.dirty || control.touched || isSubmitted));
    }
  })();

  constructor() {
    this.perfilForm = this.formBuilder.group({
      nombrePerfil: ['', [Validators.required, Validators.maxLength(100)]]
    });

    // Attach uniqueness validator
    this.perfilForm.get('nombrePerfil')!.addValidators(this.uniqueNombrePerfilValidator());
  }

  ngOnInit(): void {
    this.loadPerfiles();
    this.setupSubscriptions();
    this.setupFormSubscriptions();
  }

  ngAfterViewInit(): void {
    this.setupPaginator();
  }

  private setupPaginator(): void {
    if (this.paginator) {
      this.dataSource.paginator = this.paginator;
      this.dataSource.filterPredicate = (data: Perfil, filter: string) => {
        return data.nombrePerfil.toLowerCase().includes(filter);
      };

      this.paginator.pageSize = this.pageSize;
      this.paginator.pageIndex = this.pageIndex;
    } else {
      // Reintentar después de un tick
      setTimeout(() => this.setupPaginator(), 100);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupSubscriptions(): void {
    // Suscripción a los perfiles
    this.perfiles$.pipe(takeUntil(this.destroy$)).subscribe(perfiles => {
      this.dataSource.data = perfiles;
      this.currentPerfiles = perfiles;
      // Reconfigurar el paginator después de cargar los datos
      this.setupPaginator();
      this.cdr.markForCheck();

      // Revalidate name on data changes
      this.perfilForm.get('nombrePerfil')?.updateValueAndValidity({ onlySelf: true });
    });

    // Suscripción a errores
    this.error$.pipe(takeUntil(this.destroy$)).subscribe(error => {
      if (error) {
        // If an error occurred during deletion lifecycle, mark it so we don't show success
        if (this.isCurrentlyDeleting || this.wasDeleting) {
          this.deleteHadError = true;
          // Let global interceptor handle the UI message for HTTP errors; avoid duplicate generic
        } else {
          this.errorHandler.handleError(error);
        }
        this.store.dispatch(PerfilActions.clearPerfilError());
      }
    });

    // Suscripción a creación exitosa
    this.creating$.pipe(takeUntil(this.destroy$)).subscribe(creating => {
      if (!creating && this.wasCreating) {
        this.notificationService.showSuccessModal('Perfil creado exitosamente');

        // Guardar el estado actual de paginación antes de recargar
        const currentPageIndex = this.pageIndex;
        const currentPageSize = this.pageSize;

        this.loadPerfiles(); // Recargar la lista para mostrar el nuevo perfil

        // Restaurar la paginación después de recargar
        setTimeout(() => {
          if (this.paginator) {
            this.paginator.pageIndex = currentPageIndex;
            this.paginator.pageSize = currentPageSize;
            this.pageIndex = currentPageIndex;
            this.pageSize = currentPageSize;
          }
        }, 100);
      }
      this.wasCreating = creating;
    });

    // Suscripción a actualización exitosa
    this.updating$.pipe(takeUntil(this.destroy$)).subscribe(updating => {
      if (!updating && this.wasUpdating) {
        this.notificationService.showSuccessModal('Perfil actualizado exitosamente');
      }
      this.wasUpdating = updating;
    });

    // Suscripción a eliminación: mostrar éxito solo si no hubo error en el ciclo de borrado
    this.deleting$.pipe(takeUntil(this.destroy$)).subscribe(deleting => {
      if (!deleting && this.wasDeleting) {
        if (!this.deleteHadError) {
          this.notificationService.showSuccessModal('Perfil eliminado exitosamente');
          this.selectedPerfil = null;
        }
        // Reset flag after cycle completes
        this.deleteHadError = false;
      }
      this.isCurrentlyDeleting = deleting;
      this.wasDeleting = deleting;
    });
  }

  private setupFormSubscriptions(): void {
    const nombreCtrl = this.perfilForm.get('nombrePerfil');
    if (nombreCtrl) {
      nombreCtrl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
        if (!nombreCtrl.touched) {
          nombreCtrl.markAsTouched();
        }
        this.cdr.markForCheck();
      });
    }
  }

  private loadPerfiles(): void {
    this.store.dispatch(PerfilActions.loadPerfiles());
  }

  // Métodos de filtrado
  toggleSearch(): void {
    this.showSearchPanel = !this.showSearchPanel;
  }

  searchPerfiles(query: string): void {
    this.searchQuery = query;
    this.dataSource.filter = query.trim().toLowerCase();
  }

  onColumnFilterChange(column: string, value: string): void {
    (this.columnFilters as Record<string, string>)[column] = value;
    this.applyFilters();
  }

  resetFilters(): void {
    this.searchQuery = '';
    this.columnFilters = { nombrePerfil: '' };
    this.dataSource.filter = '';
    this.applyFilters();
  }

  private applyFilters(): void {
    const filterValue = this.columnFilters.nombrePerfil.trim().toLowerCase();
    this.dataSource.filter = filterValue;
  }

  // Métodos de modal
  openCreateModal(perfil?: Perfil): void {
    this.isEditing = !!perfil;
    this.selectedPerfil = perfil || null;

    if (perfil) {
      this.perfilForm.patchValue({
        nombrePerfil: perfil.nombrePerfil
      });
    } else {
      this.perfilForm.reset();
    }

    // Revalidate with current selection context
    this.perfilForm.get('nombrePerfil')?.updateValueAndValidity({ onlySelf: true });

    this.dialog.open(this.createPerfilModal, {
      width: '500px',
      disableClose: true
    });
  }

  closeModal(): void {
    this.dialog.closeAll();
    this.selectedPerfil = null;
    this.isEditing = false;
    this.perfilForm.reset();
  }

  // Métodos de formulario
  async savePerfil(): Promise<void> {
    if (this.perfilForm.valid) {
      const formData = this.perfilForm.value;
      this.logger.info('Saving perfil:', formData);

      if (this.isEditing && this.selectedPerfil) {
        // Para editar, mostrar confirmación primero
        const confirmed = await this.notificationService.showConfirmation(
          '¿Está seguro de que desea actualizar este perfil?',
          'Confirmar Actualización'
        );

        if (confirmed) {
          this.store.dispatch(
            PerfilActions.updatePerfil({
              id: this.selectedPerfil.perfilId,
              perfil: { Nombre_perfil: formData.nombrePerfil }
            })
          );
        }
      } else {
        this.store.dispatch(
          PerfilActions.createPerfil({
            perfil: { Nombre_perfil: formData.nombrePerfil }
          })
        );
      }
    } else {
      this.markFormGroupTouched(this.perfilForm);
    }
  }

  // Métodos de acciones
  selectPerfil(perfil: Perfil): void {
    this.selectedPerfil = perfil;
    this.store.dispatch(PerfilActions.selectPerfil({ perfil }));
  }

  async deletePerfil(): Promise<void> {
    if (this.selectedPerfil) {
      this.logger.info('Deleting perfil:', this.selectedPerfil);

      const confirmed = await this.notificationService.showConfirmation(
        '¿Está seguro de que desea eliminar este perfil?',
        'Confirmar Eliminación'
      );

      if (confirmed) {
        this.store.dispatch(PerfilActions.deletePerfil({ id: this.selectedPerfil.perfilId }));
      }
    }
  }

  // Métodos de utilidad
  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();
    });
  }

  hasError(fieldName: string): boolean {
    const control = this.perfilForm.get(fieldName);
    return !!(control && control.invalid && (control.dirty || control.touched));
  }

  getErrorMessage(fieldName: string): string {
    const control = this.perfilForm.get(fieldName);
    if (control?.errors) {
      if (control.errors['required']) {
        return 'Este campo es requerido';
      }
      if (control.errors['maxlength']) {
        return `Máximo ${control.errors['maxlength'].requiredLength} caracteres`;
      }
      if (control.errors['nameExists']) {
        return 'Ya existe un perfil con este nombre';
      }
    }
    return '';
  }

  // Método para el paginador
  onPageChange(event: { pageIndex: number; pageSize: number; length: number }): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.cdr.markForCheck();
  }

  // Custom validators
  private uniqueNombrePerfilValidator(): ValidatorFn {
    return (control: AbstractControl) => {
      const rawValue = (control.value || '').toString().trim().toLowerCase();
      if (!rawValue) {
        return null;
      }

      const isDuplicate = this.currentPerfiles.some(p => {
        const matchesName = (p.nombrePerfil || '').trim().toLowerCase() === rawValue;
        const isSameEditing =
          this.isEditing && this.selectedPerfil && p.perfilId === this.selectedPerfil.perfilId;
        return matchesName && !isSameEditing;
      });

      return isDuplicate ? { nameExists: true } : null;
    };
  }
}
